import Component from '@glimmer/component';
import { action } from '@ember/object';
import Swal from 'sweetalert2';
import onInsert from 'scaleflow/modifiers/render-modifiers/on-insert';

/**
 * Local replacement for the unmaintained `ember-sweetalert` addon, which cannot
 * run on Ember 7: it imports `inject` from '@ember/service', and calling that
 * throws from 7.0 onwards.
 *
 * The addon forwarded ~60 SweetAlert2 config keys, but every call site in this
 * app uses only @title, @text, @icon and @willClose. The lists below cover that
 * usage plus the near neighbours — extend them if a new call site needs more.
 */
const CONFIG_KEYS = [
  'title',
  'titleText',
  'text',
  'html',
  'icon',
  'iconColor',
  'footer',
  'timer',
  'showConfirmButton',
  'confirmButtonText',
  'showCancelButton',
  'cancelButtonText',
] as const;

const EVENT_KEYS = ['willOpen', 'didOpen', 'willClose', 'didClose'] as const;

type SweetAlertCallback = (...args: unknown[]) => void;

export interface SweetAlertSignature {
  Element: HTMLSpanElement;
  Args: {
    Named: {
      title?: string;
      titleText?: string;
      text?: string;
      html?: string;
      icon?: 'success' | 'error' | 'warning' | 'info' | 'question';
      iconColor?: string;
      footer?: string;
      timer?: number;
      showConfirmButton?: boolean;
      confirmButtonText?: string;
      showCancelButton?: boolean;
      cancelButtonText?: string;
      /** Defaults to true — every current call site gates on an {{#if}} instead. */
      show?: boolean;
      willOpen?: SweetAlertCallback;
      didOpen?: SweetAlertCallback;
      willClose?: SweetAlertCallback;
      didClose?: SweetAlertCallback;
      onConfirm?: (result: unknown) => void;
      onCancel?: (result: unknown) => void;
    };
  };
}

export default class SweetAlert extends Component<SweetAlertSignature> {
  get isOpen(): boolean {
    return this.args.show ?? true;
  }

  @action
  async fire(): Promise<void> {
    if (!this.isOpen) {
      return;
    }

    const result = await Swal.fire(this.options());

    if (result?.value) {
      this.call('onConfirm', result);
    } else {
      this.call('onCancel', result);
    }
  }

  private call(
    name: keyof SweetAlertSignature['Args']['Named'],
    ...args: unknown[]
  ): void {
    const callback = this.args[name];

    if (!this.isDestroying && typeof callback === 'function') {
      (callback as SweetAlertCallback)(...args);
    }
  }

  private options(): Record<string, unknown> {
    const options: Record<string, unknown> = {};

    for (const key of CONFIG_KEYS) {
      if (this.args[key] !== undefined) {
        options[key] = this.args[key];
      }
    }

    for (const key of EVENT_KEYS) {
      if (typeof this.args[key] === 'function') {
        options[key] = (...args: unknown[]) => this.call(key, ...args);
      }
    }

    return options;
  }

  <template>
    <span aria-hidden="true" {{onInsert this.fire}} ...attributes></span>
  </template>
}
